import { currencies, formatCurrency } from "@gymflow/helpers";
import { createColumnHelper } from "@tanstack/react-table";
import { useClubSettings } from "apps/portal/src/providers";
import useGymflowModels from "apps/portal/src/store";
import { useStaffGetInvoiceReceipt } from "libs/api/src/lib/hooks/staff/useStaffGetInvoiceReceipt";
import { useTranslation } from "react-i18next";

import { Table } from "../../../organisms/Table";

type InvoiceItemsTableProps = {
  invoiceNumber: string;
};

type InvoiceItem = {
  item: string;
  qty: number;
  priceExcTax: string;
  salesTaxPercent: string;
  tax: string;
  total: string;
};

const InvoiceItemsTable = ({ invoiceNumber }: InvoiceItemsTableProps) => {
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { club } = useClubSettings();

  const { data: invoice } = useStaffGetInvoiceReceipt({
    api,
    invoiceNumber,
  });

  if (!invoice) return null;

  const columnHelper = createColumnHelper<InvoiceItem>();

  const columns = [
    columnHelper.accessor("item", {
      header: () => t("components.paymentsSidebarProvider.invoice.item"),
      enableSorting: false,
    }),
    columnHelper.accessor("qty", {
      header: () => t("components.paymentsSidebarProvider.invoice.qty"),
      enableSorting: false,
    }),
    columnHelper.accessor("priceExcTax", {
      header: () => t("components.paymentsSidebarProvider.invoice.exTax"),
      enableSorting: false,
    }),
    columnHelper.accessor("salesTaxPercent", {
      header: () =>
        t("components.paymentsSidebarProvider.invoice.taxPercentage"),
      enableSorting: false,
    }),
    columnHelper.accessor("tax", {
      header: () =>
        t("components.paymentsSidebarProvider.invoice.tax", {
          symbol: currencies[club.defaultCurrency].symbol,
        }),
      enableSorting: false,
    }),
    columnHelper.accessor("total", {
      header: () => t("components.paymentsSidebarProvider.invoice.incTax"),
      enableSorting: false,
    }),
  ];

  const dataItems = invoice?.items.map((item) => ({
    item: item.name,
    qty: item.quantity,
    priceExcTax: formatCurrency(item.priceExTax, club.defaultCurrency),
    salesTaxPercent: item.taxRate,
    tax: formatCurrency(
      item.priceIncTax - item.priceExTax,
      club.defaultCurrency,
    ),
    total: formatCurrency(item.priceIncTax, club.defaultCurrency),
  }));

  return (
    <div className="mt-6 overflow-x-auto rounded-t-2xl border border-gray-200 md:rounded-bl-2xl">
      <Table
        data={dataItems}
        columns={columns}
        isFetching={false}
        className="!relative w-full"
        rowClassName="h-10 border-b last:border-b-0"
        thClassName="text-xs"
      />
    </div>
  );
};

export default InvoiceItemsTable;
